import NiceModal, { useModal } from "@ebay/nice-modal-react";
import cx from "classnames";
import DOMPurify from "dompurify";
import React from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENT_SEND_V2, CHAMPIONS_POPUP_SET_ACTIVE_V2 } from "@/api/apiEndpoints";
import { ROOT_STATIC_MEDIA_PATH_V2 } from "@/api/staticPaths";
import { canv } from "@/canv/global";
import { MakeRequest } from "@/canv/utils";
import { SNACKBAR_TIME } from "@/elements/text/constants";
import { showSnackbar } from "@/snackbar/snackbar";
import styles from "./ChampionPopup.module.scss";

export const ChampionPopup = NiceModal.create(
    ({ link, template, popupUID }: { link: string; template: string; popupUID: string }) => {
        const { t } = useTranslation();
        const modal = useModal();

        const switchActiveFlag = async () => {
            try {
                await MakeRequest(
                    CHAMPIONS_POPUP_SET_ACTIVE_V2,
                    {
                        token: canv.getInitDataProp("userToken"),
                        popupUID: popupUID,
                    },
                    false,
                    true,
                );
                modal.resolve();
            } catch (err) {
                showSnackbar(t("share.serverError"), { type: "success", duration: SNACKBAR_TIME });
            } finally {
                modal.remove();
            }
        };

        const sendAnalytics = async (action: string) => {
            try {
                await MakeRequest(
                    ANALYTICS_EVENT_SEND_V2,
                    {
                        token: canv.getInitDataProp("userToken"),
                        eventType: "boards_jespo_champion_popup",
                        properties: { action, popupUID },
                    },
                    false,
                    true,
                );
            } catch (err) {
                console.error("Analytics request failed:", err);
            }
        };

        const handleGetAccess = async () => {
            if (link) {
                window.open(link, "_blank", "noopener,noreferrer");
            }
            void sendAnalytics("get_access");
            await switchActiveFlag();
        };
        const handleNextTime = async () => {
            void sendAnalytics("later");
            await switchActiveFlag();
        };
        const handleCloseModal = async () => {
            void sendAnalytics("close");
            await switchActiveFlag();
        };

        const sanitizedTemplate = DOMPurify.sanitize(template);

        return (
            <div className={styles.modalWrapper}>
                <div className={styles.modal}>
                    <div className={styles.modalHeader}>
                        <div className={styles.closeWrapper}>
                            <button
                                className={cx("button--close", styles.close)}
                                type="button"
                                aria-label={t("common.close")}
                                onClick={handleCloseModal}
                            />
                        </div>
                    </div>
                    <div className={styles.modalMain}>
                        <div className={styles.imgWrapper}>
                            <img
                                src={`${canv.host}${ROOT_STATIC_MEDIA_PATH_V2}championPopupImg.png`}
                                alt="illustration"
                            />
                        </div>
                        {/** biome-ignore lint/security/noDangerouslySetInnerHtml: <explanation> */}
                        <div dangerouslySetInnerHTML={{ __html: sanitizedTemplate }} />
                    </div>

                    <div className={styles.modalFooter}>
                        <button
                            type={"button"}
                            className={styles.complete}
                            onClick={handleGetAccess}
                        >
                            {t("championPopup.getAccess")}
                        </button>
                        <button type={"button"} className={styles.back} onClick={handleNextTime}>
                            {t("championPopup.anotherTime")}
                        </button>
                    </div>
                </div>
            </div>
        );
    },
);
